import React from 'react';
import { Box, Tooltip } from '@mui/material';

/**
 * Status colors:
 * - Online (green): User is actively connected
 * - Away (yellow): User recently disconnected (within 5 min grace period)
 * - Offline (gray): User has been gone for more than 5 minutes
 */
const STATUS_COLORS = {
  online: '#22c55e',  // Green
  away: '#eab308',    // Yellow/Amber
  offline: '#9ca3af'  // Gray
};

const STATUS_LABELS = {
  online: 'Online',
  away: 'Abwesend',
  offline: 'Offline'
};

/**
 * OnlineStatusDot - Shows user's online/away/offline status
 *
 * @param {string} status - 'online' | 'away' | 'offline' (new format)
 * @param {boolean} isOnline - Legacy prop, will be converted to status if status not provided
 * @param {string} lastSeenAt - ISO timestamp of last activity
 * @param {number} size - Dot size in pixels
 */
const OnlineStatusDot = ({ status, isOnline, lastSeenAt, size = 10 }) => {
  // Support both new status prop and legacy isOnline prop
  const computedStatus = status || (isOnline ? 'online' : 'offline');

  const formatLastSeen = (timestamp) => {
    if (!timestamp) return STATUS_LABELS[computedStatus];

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `Vor ${diffMins} Min`;
    if (diffHours < 24) return `Vor ${diffHours} Std`;
    if (diffDays < 7) return `Vor ${diffDays} Tagen`;
    return date.toLocaleDateString('de-DE');
  };

  // Build tooltip text
  let tooltipTitle;
  if (computedStatus === 'online') {
    tooltipTitle = STATUS_LABELS.online;
  } else if (computedStatus === 'away') {
    tooltipTitle = `${STATUS_LABELS.away} - ${formatLastSeen(lastSeenAt)}`;
  } else {
    tooltipTitle = `${STATUS_LABELS.offline} - ${formatLastSeen(lastSeenAt)}`;
  }

  return (
    <Tooltip title={tooltipTitle} arrow>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: STATUS_COLORS[computedStatus] || STATUS_COLORS.offline,
          border: '2px solid white',
          boxShadow: 1
        }}
      />
    </Tooltip>
  );
};

export default OnlineStatusDot;
